import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Typography from '@/components/ui/typography';
import AppLayout from '@/components/layout/AppLayout';
import Navbar from '@/components/ui/navbar';
import InfiniteScrollList from '@/components/ui/InfiniteScrollList';
import BatchCheckCard from './batchCheck/BatchCheckCard';
import UploadFileDialog from './batchCheck/UploadFileDialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { BatchCheckItem, BatchJobStatus } from './batchCheck/types';
import { useHospital } from '@/hooks/useHospital';
import {
  batchJobService,
  type BatchJobModel,
  type BatchCheckType,
} from '@/services/batchJobService';
import { useToastHelpers } from '@/utils/toast';

const BATCH_JOBS_PAGE_SIZE = 10;

const BATCH_JOBS_POLL_INTERVAL_MS = 30_000;

const TERMINAL_BATCH_JOB_STATUSES: BatchJobStatus[] = [
  'COMPLETED',
  'FAILED',
  'CANCELLED',
];

// Helper function to format date and time
const formatDateAndTime = (
  dateString: string | null,
): { date: string; time: string } => {
  if (!dateString) {
    return { date: '', time: '' };
  }

  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const minutesStr = minutes.toString().padStart(2, '0');

  return {
    date: `${month}-${day}-${year}`,
    time: `${hours}:${minutesStr}${ampm}`,
  };
};

// Helper function to map API model to BatchCheckItem
const mapBatchJobToItem = (model: BatchJobModel): BatchCheckItem => {
  const successCount = model.success ?? 0;

  const { date: completedDate, time: completedTime } = formatDateAndTime(
    model.completedAt,
  );
  const { date: createdDate, time: createdTime } = formatDateAndTime(
    model.createdAt ?? null,
  );

  // Map API status to BatchJobStatus type
  const status = model.status as BatchCheckItem['status'];

  return {
    id: model.batchJobId,
    title: model.name,
    progress: model.progressPercentage,
    success: successCount,
    failed: model.failed ?? 0,
    total: model.total ?? 0,
    skipped: model.skipped ?? 0,
    createdDate,
    createdTime,
    completedDate,
    completedTime,
    status,
    completionStatus: model.completionStatus ?? '',
  };
};

const BatchCheck: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cancelConfirmJobId, setCancelConfirmJobId] = useState<string | null>(
    null,
  );
  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);
  const [batchChecks, setBatchChecks] = useState<BatchCheckItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLast, setIsLast] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [initialLoadError, setInitialLoadError] = useState<string | null>(null);
  const { getSelectedHospitalData } = useHospital();
  const { showSuccess, showError } = useToastHelpers();

  // Get tenantId from selected hospital
  const hospitalData = getSelectedHospitalData();
  const tenantId = hospitalData?.tenantId;

  const fetchBatchJobsPage = useCallback(
    async (pageNo: number, append: boolean, options?: { silent?: boolean }) => {
      if (!tenantId) {
        setIsLoading(false);
        return;
      }

      const silent = options?.silent ?? false;

      try {
        if (append) {
          setIsLoadingMore(true);
        } else if (!silent) {
          setIsLoading(true);
          setInitialLoadError(null);
        }

        const response = await batchJobService.getBatchJobsList(
          tenantId,
          pageNo,
          BATCH_JOBS_PAGE_SIZE,
          hospitalData?.ehr,
        );
        const mappedItems = response.data.models.map(mapBatchJobToItem);
        setBatchChecks((prev) =>
          append ? [...prev, ...mappedItems] : mappedItems,
        );
        setIsLast(response.data.isLast);
        setCurrentPage(pageNo);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to fetch batch jobs. Please try again.';
        if (append) {
          showError(errorMessage);
        } else if (silent) {
          showError(errorMessage);
        } else {
          setInitialLoadError(errorMessage);
          setBatchChecks([]);
          setIsLast(true);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    // showError from useToastHelpers is a new function each render; including it
    // recreates this callback every render and retriggers the mount effect → infinite requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId],
  );

  const refreshBatchJobs = useCallback(() => {
    return fetchBatchJobsPage(0, false, { silent: true });
  }, [fetchBatchJobsPage]);

  const handleLoadMore = useCallback(() => {
    if (!tenantId || isLoadingMore || isLast) return;
    void fetchBatchJobsPage(currentPage + 1, true);
  }, [tenantId, isLoadingMore, isLast, currentPage, fetchBatchJobsPage]);

  useEffect(() => {
    if (!tenantId) {
      setIsLoading(false);
      setBatchChecks([]);
      setIsLast(true);
      setCurrentPage(0);
      setInitialLoadError(null);
      return;
    }
    void fetchBatchJobsPage(0, false);
    // fetchBatchJobsPage is stable for a given tenantId (see callback deps above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const hasActiveBatchJobs = batchChecks.some(
    (check) => !TERMINAL_BATCH_JOB_STATUSES.includes(check.status),
  );

  useEffect(() => {
    if (!tenantId || !hasActiveBatchJobs) return;

    const intervalId = setInterval(() => {
      void refreshBatchJobs();
    }, BATCH_JOBS_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [tenantId, hasActiveBatchJobs, refreshBatchJobs]);

  const handleCreateBatch = async (
    batchName: string,
    file: File,
    checkType: BatchCheckType,
  ) => {
    if (!tenantId) {
      showError('Please select a hospital');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await batchJobService.createBatchJob(
        tenantId,
        batchName,
        file,
        checkType,
        hospitalData?.ehr,
      );
      showSuccess(
        response.message ||
          `Batch job "${batchName}" created successfully with ${response.data.totalAppointments} appointments`,
      );
      // Refresh batch checks list after successful creation
      await refreshBatchJobs();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create batch job. Please try again.';
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleBatch = async (
    batchName: string,
    file: File,
    scheduledAt: string,
    checkType: BatchCheckType,
  ) => {
    if (!tenantId) {
      showError('Please select a hospital');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await batchJobService.scheduleBatchJob(
        tenantId,
        batchName,
        file,
        scheduledAt,
        checkType,
        hospitalData?.ehr,
      );

      showSuccess(
        response.message || `Batch job "${batchName}" scheduled successfully`,
      );

      await refreshBatchJobs();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to schedule batch job. Please try again.';
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelClick = (id: string) => {
    setCancelConfirmJobId(id);
  };

  const handleCancelConfirm = async () => {
    const id = cancelConfirmJobId;
    if (!id || !tenantId) {
      setCancelConfirmJobId(null);
      if (!tenantId) showError('Please select a hospital');
      return;
    }

    setCancelConfirmJobId(null);
    setCancellingJobId(id);
    try {
      await batchJobService.cancelBatchJob(tenantId, id, hospitalData?.ehr);
      showSuccess('Batch job cancelled successfully');
      await refreshBatchJobs();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to cancel batch job. Please try again.';
      showError(errorMessage);
    } finally {
      setCancellingJobId(null);
    }
  };

  const handleCancelConfirmClose = (open: boolean) => {
    if (!open) setCancelConfirmJobId(null);
  };

  const handleDownload = async (id: string) => {
    if (!tenantId) {
      showError('Please select a hospital');
      return;
    }

    try {
      const blob = await batchJobService.downloadBatchJobResults(
        tenantId,
        id,
        hospitalData?.ehr,
      );

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Find the batch check item to use its name for the filename
      const batchCheck = batchChecks.find((check) => check.id === id);
      const fileName = batchCheck
        ? `${batchCheck.title}_results.csv`
        : `batch_job_${id}_results.csv`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('Batch job results downloaded successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to download batch job results. Please try again.';
      showError(errorMessage);
    }
  };

  return (
    <AppLayout currentPage="Batch Check">
      <Navbar showSearch={true} showAvatar={true} showWelcomeMessage={true} />
      <div className="px-6 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <Typography variant="h1" color="neutral1000" weight="bold" size="24">
            Past batch checks
          </Typography>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="text-gray-700 border border-gray-300 hover:bg-gray-100 h-10 px-4 rounded-md"
          >
            New batch check
            <Plus className="h-4 w-4 ml-1" />
          </Button>
        </div>

        {/* Batch Check Cards */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Typography variant="p" color="neutral700" size="14">
              Loading batch checks...
            </Typography>
          </div>
        ) : initialLoadError ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Typography variant="p" color="neutral700" size="14">
              {initialLoadError}
            </Typography>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void fetchBatchJobsPage(0, false)}
            >
              Try again
            </Button>
          </div>
        ) : batchChecks.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Typography variant="p" color="neutral700" size="14">
              No batch checks found. Create a new batch check to get started.
            </Typography>
          </div>
        ) : (
          <InfiniteScrollList
            loading={false}
            loadingMore={isLoadingMore}
            error={null}
            hasMore={!isLast}
            onLoadMore={handleLoadMore}
            className="max-h-[calc(100vh-300px)] overflow-y-auto scrollbar-hide [&::-webkit-scrollbar]:hidden"
          >
            <div className="space-y-4">
              {batchChecks.map((check) => (
                <BatchCheckCard
                  key={check.id}
                  check={check}
                  onDownload={handleDownload}
                  onCancel={handleCancelClick}
                  isCancelling={cancellingJobId === check.id}
                />
              ))}
            </div>
          </InfiniteScrollList>
        )}

        {/* Cancel Confirmation Dialog */}
        <Dialog
          open={!!cancelConfirmJobId}
          onOpenChange={handleCancelConfirmClose}
        >
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="text-left">
                <Typography
                  variant="h2"
                  weight="semibold"
                  className="text-neutral-900"
                >
                  Cancel batch check
                </Typography>
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Typography variant="small" className="text-neutral-600">
                Are you sure you want to cancel this batch check? This can't be
                undone.
              </Typography>
            </div>
            <DialogFooter className="flex gap-3">
              <Button
                variant="destructive"
                onClick={handleCancelConfirm}
                className="flex-1 sm:flex-none"
                disabled={!!cancellingJobId}
              >
                {cancellingJobId ? 'Cancelling...' : 'Yes'}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleCancelConfirmClose(false)}
                className="flex-1 sm:flex-none"
                disabled={!!cancellingJobId}
              >
                No
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload File Dialog */}
        <UploadFileDialog
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onSubmit={handleCreateBatch}
          onScheduleSubmit={handleScheduleBatch}
          isSubmitting={isSubmitting}
        />
      </div>
    </AppLayout>
  );
};

export default BatchCheck;
